// Règles des messages, sans dépendance : testables seules.
//
// Au démarrage, la liste des commandes ne reçoit que les messages PAS ENCORE
// LUS par moi, et sans leur texte : ce sont les seuls qui comptent dans les
// pastilles. Le fil complet d'une commande arrive quand on l'ouvre
// (commande.messagesComplets = true).

// Tout ce que lisent les pastilles, la visibilité et le marquage « lu ».
export const COLONNES_MESSAGES_LEGERES = "id, commande_id, auteur, lu_par, visible_par, portee, created_at";

// Filtre PostgREST (table jointe « messages ») : lu_par nul, ou sans mon nom.
// Un nom avec un caractère qui casserait la syntaxe → pas de filtre : on
// charge tous les messages légers (plus lourd, mais aucune pastille perdue).
export function filtreNonLus(nom) {
  if (!nom || /[,(){}"\\]/.test(nom)) return null;
  return `lu_par.is.null,lu_par.not.cs.{"${nom}"}`;
}

const parDate = (a, b) => new Date(a.created_at) - new Date(b.created_at);
const estComplet = m => Object.prototype.hasOwnProperty.call(m, "texte");
const union = (a, b) => [...new Set([...(a || []), ...(b || [])])];

export function normaliserMessages(liste, peutVoir) {
  return (liste || []).filter(peutVoir).sort(parDate);
}

// Fusion par identifiant : la version complète l'emporte, lu_par est uni.
export function fusionnerMessages(existants, arrivants) {
  const parId = new Map((existants || []).map(m => [m.id, m]));
  for (const a of arrivants || []) {
    const e = parId.get(a.id);
    if (!e) { parId.set(a.id, a); continue; }
    const fusion = estComplet(a) || !estComplet(e) ? { ...e, ...a } : { ...a, ...e };
    parId.set(a.id, { ...fusion, lu_par: union(e.lu_par, a.lu_par) });
  }
  return [...parId.values()].sort(parDate);
}

// Rechargement général : la commande `prec` (en mémoire) reçoit `arrivants`
// (ses messages non lus, légers).
// - fil gardé (commande regardée, fil complet) : on garde le fil (seuls les
//   messages déjà connus sont mis à jour) ; les nouveaux arrivent complets
//   avec le rechargement du fil qui suit ;
// - sinon : la liste reçue fait foi (un message lu ailleurs sort), mais on
//   garde un « lu » posé ici entre-temps.
export function messagesApresRechargement(prec, arrivants, garderFil) {
  if (!prec) return [...(arrivants || [])].sort(parDate);
  if (garderFil && prec.messagesComplets) {
    const connus = new Set((prec.messages || []).map(x => x.id));
    return fusionnerMessages(prec.messages, (arrivants || []).filter(a => connus.has(a.id)));
  }
  const ancien = new Map((prec.messages || []).map(m => [m.id, m]));
  return (arrivants || [])
    .map(a => (ancien.has(a.id) ? { ...a, lu_par: union(ancien.get(a.id).lu_par, a.lu_par) } : a))
    .sort(parDate);
}

// Fil complet reçu du serveur : il fait foi (un message supprimé ailleurs
// disparaît), en gardant un « lu » posé ici et les messages arrivés en direct
// pendant le chargement : complets (texte connu) et plus récents que le
// dernier message reçu — ou, si le fil reçu est vide, que le début de la
// demande (`debut`, en ms) moins une minute d'écart d'horloge.
export function remplacerParComplets(existants, complets, debut) {
  const ancien = new Map((existants || []).map(m => [m.id, m]));
  const recus = (complets || []).map(c => (ancien.has(c.id) ? { ...c, lu_par: union(ancien.get(c.id).lu_par, c.lu_par) } : c));
  const idsRecus = new Set(recus.map(c => c.id));
  const seuil = recus.length
    ? recus.reduce((max, c) => Math.max(max, new Date(c.created_at).getTime()), -Infinity)
    : debut - 60000;
  const direct = (existants || []).filter(e =>
    !idsRecus.has(e.id) && estComplet(e) && new Date(e.created_at).getTime() > seuil
  );
  return [...recus, ...direct].sort(parDate);
}
